import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Checkbox,
  InputBase,
} from "@mui/material";
import {
  PublicOutlined,
  LockOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseOutlined,
} from "@mui/icons-material";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../shared/themes/redesignTokens";
import partnershipsApi from "../../partnerships/api/partnershipsApi";
import { PartnerRelationship } from "../../partnerships/models/models";
import { CustomerSettingsState } from "../../settings/models/models";

// ═══════════════════════════════════════════════════════════════
//  Props
// ═══════════════════════════════════════════════════════════════

interface PublishServiceRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onPublish: (visibilityCode: number, targetPartnerIds: string[]) => Promise<void>;
}

// ═══════════════════════════════════════════════════════════════
//  Helper: get partner name + logo from a relationship
//  (Partner is the OTHER company, not the current user's company)
// ═══════════════════════════════════════════════════════════════

interface PartnerInfo {
  id: string;
  name: string;
  logoUrl?: string;
}

function getPartnerInfos(partnerships: PartnerRelationship[], currentCustomerId: string): PartnerInfo[] {
  if (partnerships.length === 0) return [];

  const partners = partnerships
    .filter((p) => p.isActive)
    .map((p) => {
      const isA = p.customerIdA?.toLowerCase() === currentCustomerId?.toLowerCase();
      return {
        id: isA ? p.customerIdB : p.customerIdA,
        name: isA ? p.customerNameB : p.customerNameA,
        logoUrl: isA ? p.customerLogoUrlB : p.customerLogoUrlA,
      };
    });

  // Deduplicate by partner ID
  return Array.from(new Map(partners.map((p) => [p.id, p])).values());
}

// ═══════════════════════════════════════════════════════════════
//  Component
// ═══════════════════════════════════════════════════════════════

const PublishServiceRequestDialog: React.FC<PublishServiceRequestDialogProps> = ({
  open,
  onClose,
  onPublish,
}) => {
  const { translate } = useTranslation(["servicing", "common"]);
  const { customer: ownCustomer } = useAppSelector<CustomerSettingsState>(
    (state: any) => state.customerSettings
  );

  const [visibility, setVisibility] = useState<1 | 2>(1); // 1=public, 2=private
  const [selectedPartnerIds, setSelectedPartnerIds] = useState<string[]>([]);
  const [partners, setPartners] = useState<PartnerInfo[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Fetch partners when dialog opens
  useEffect(() => {
    if (!open) {
      // Reset state when closing
      setVisibility(1);
      setSelectedPartnerIds([]);
      setSearchValue("");
      return;
    }

    const fetchPartners = async () => {
      setPartnersLoading(true);
      try {
        const result = await partnershipsApi.getPartnerships({
          pageSize: 100,
          pageNo: 1,
        });
        setPartners(getPartnerInfos(result.items || [], ownCustomer?.id || ""));
      } catch {
        setPartners([]);
      } finally {
        setPartnersLoading(false);
      }
    };

    fetchPartners();
  }, [open]);

  const filteredPartners = useMemo(() => {
    if (!searchValue.trim()) return partners;
    const query = searchValue.toLowerCase();
    return partners.filter((p) => p.name.toLowerCase().includes(query));
  }, [partners, searchValue]);

  const handleTogglePartner = (partnerId: string) => {
    setSelectedPartnerIds((prev) =>
      prev.includes(partnerId)
        ? prev.filter((id) => id !== partnerId)
        : [...prev, partnerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPartnerIds.length === partners.length) {
      setSelectedPartnerIds([]);
    } else {
      setSelectedPartnerIds(partners.map((p) => p.id));
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await onPublish(
        visibility,
        visibility === 2 ? selectedPartnerIds : []
      );
      onClose();
    } finally {
      setPublishing(false);
    }
  };

  const canPublish =
    visibility === 1 || (visibility === 2 && selectedPartnerIds.length > 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          fontFamily: dt.font,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: dt.font,
          fontWeight: 700,
          fontSize: "18px",
          color: dt.textPrimary,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        {translate("servicing:card.publishDialog.title")}
        <Box
          onClick={onClose}
          sx={{
            cursor: "pointer",
            color: dt.textMuted,
            "&:hover": { color: dt.textPrimary },
            transition: "color 0.2s",
          }}
        >
          <CloseOutlined sx={{ fontSize: "20px" }} />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: "8px !important" }}>
        {/* Visibility options */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mb: 2 }}>
          {/* Public option */}
          <Box
            onClick={() => setVisibility(1)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "14px 18px",
              borderRadius: "12px",
              border: `2px solid ${visibility === 1 ? dt.blue : dt.cardBorder}`,
              backgroundColor: visibility === 1 ? `${dt.blue}08` : "transparent",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: visibility === 1 ? dt.blue : "#94A3B8",
              },
            }}
          >
            <PublicOutlined
              sx={{
                fontSize: "28px",
                color: visibility === 1 ? dt.blue : dt.textMuted,
                transition: "color 0.2s",
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: dt.textPrimary,
                  fontFamily: dt.font,
                }}
              >
                {translate("servicing:card.publishDialog.public")}
              </Box>
              <Box
                sx={{
                  fontSize: "12px",
                  color: dt.textSecondary,
                  fontFamily: dt.font,
                  mt: "2px",
                }}
              >
                {translate("servicing:card.publishDialog.publicDesc")}
              </Box>
            </Box>
            {visibility === 1 && (
              <CheckCircleOutlined sx={{ fontSize: "20px", color: dt.blue }} />
            )}
          </Box>

          {/* Private option */}
          <Box
            onClick={() => setVisibility(2)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "14px 18px",
              borderRadius: "12px",
              border: `2px solid ${visibility === 2 ? "#27AE60" : dt.cardBorder}`,
              backgroundColor: visibility === 2 ? "rgba(39,174,96,0.05)" : "transparent",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: visibility === 2 ? "#27AE60" : "#94A3B8",
              },
            }}
          >
            <LockOutlined
              sx={{
                fontSize: "28px",
                color: visibility === 2 ? "#27AE60" : dt.textMuted,
                transition: "color 0.2s",
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: dt.textPrimary,
                  fontFamily: dt.font,
                }}
              >
                {translate("servicing:card.publishDialog.private")}
              </Box>
              <Box
                sx={{
                  fontSize: "12px",
                  color: dt.textSecondary,
                  fontFamily: dt.font,
                  mt: "2px",
                }}
              >
                {translate("servicing:card.publishDialog.privateDesc")}
              </Box>
            </Box>
            {visibility === 2 && (
              <CheckCircleOutlined sx={{ fontSize: "20px", color: "#27AE60" }} />
            )}
          </Box>
        </Box>

        {/* Partner picker (when private is selected) */}
        {visibility === 2 && (
          <Box
            sx={{
              border: `1px solid ${dt.cardBorder}`,
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px",
                backgroundColor: "#F8FAFC",
                borderBottom: `1px solid ${dt.cardBorder}`,
              }}
            >
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: dt.textSecondary,
                  fontFamily: dt.font,
                }}
              >
                {translate("servicing:card.publishDialog.selectPartners")}
              </Box>
              {partners.length > 0 && (
                <Box
                  onClick={handleSelectAll}
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: dt.blue,
                    cursor: "pointer",
                    fontFamily: dt.font,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {selectedPartnerIds.length === partners.length
                    ? translate("servicing:visibility.deselectAll")
                    : translate("servicing:visibility.selectAll")}
                </Box>
              )}
            </Box>

            {/* Search */}
            {partners.length > 3 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 14px",
                  borderBottom: `1px solid ${dt.cardBorder}`,
                }}
              >
                <SearchOutlined sx={{ fontSize: "16px", color: dt.textMuted }} />
                <InputBase
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={translate("servicing:visibility.searchPartners")}
                  sx={{
                    flex: 1,
                    fontSize: "13px",
                    fontFamily: dt.font,
                    color: dt.textPrimary,
                  }}
                />
              </Box>
            )}

            {/* Partner list */}
            <Box sx={{ maxHeight: "200px", overflowY: "auto" }}>
              {partnersLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 3,
                  }}
                >
                  <CircularProgress size={24} sx={{ color: dt.blue }} />
                </Box>
              ) : partners.length === 0 ? (
                <Box
                  sx={{
                    padding: "20px 14px",
                    textAlign: "center",
                    color: dt.textMuted,
                    fontSize: "13px",
                    fontFamily: dt.font,
                  }}
                >
                  {translate("servicing:card.publishDialog.noPartners")}
                </Box>
              ) : (
                filteredPartners.map((partner) => {
                  const isChecked = selectedPartnerIds.includes(partner.id);
                  return (
                    <Box
                      key={partner.id}
                      onClick={() => handleTogglePartner(partner.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 14px",
                        cursor: "pointer",
                        transition: "background-color 0.15s",
                        borderBottom: `1px solid ${dt.rowBorder}`,
                        backgroundColor: isChecked ? "rgba(39,174,96,0.04)" : "transparent",
                        "&:hover": {
                          backgroundColor: isChecked
                            ? "rgba(39,174,96,0.08)"
                            : dt.rowHover,
                        },
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Checkbox
                        checked={isChecked}
                        size="small"
                        sx={{
                          color: dt.cardBorder,
                          "&.Mui-checked": { color: "#27AE60" },
                          padding: "2px",
                        }}
                      />
                      {/* Partner logo */}
                      {partner.logoUrl ? (
                        <Box
                          component="img"
                          src={partner.logoUrl}
                          alt=""
                          sx={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: `1px solid ${dt.cardBorder}`,
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: dt.blueSoft,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            sx={{
                              fontSize: "13px",
                              fontWeight: 700,
                              color: dt.blue,
                              fontFamily: dt.font,
                            }}
                          >
                            {partner.name?.charAt(0)?.toUpperCase() || "?"}
                          </Box>
                        </Box>
                      )}
                      <Box
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: dt.textPrimary,
                          fontFamily: dt.font,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                        }}
                      >
                        {partner.name}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Selected count */}
            {selectedPartnerIds.length > 0 && (
              <Box
                sx={{
                  padding: "8px 14px",
                  borderTop: `1px solid ${dt.cardBorder}`,
                  backgroundColor: "#F8FAFC",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#27AE60",
                  fontFamily: dt.font,
                }}
              >
                {translate("servicing:visibility.partnersSelected", {
                  count: selectedPartnerIds.length,
                })}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px", gap: "8px" }}>
        <Button
          onClick={onClose}
          disabled={publishing}
          sx={{
            textTransform: "none",
            fontFamily: dt.font,
            fontWeight: 600,
            fontSize: "13px",
            color: dt.textSecondary,
            borderRadius: "8px",
          }}
        >
          {translate("common:buttons.cancel")}
        </Button>
        <Button
          onClick={handlePublish}
          disabled={!canPublish || publishing}
          variant="contained"
          startIcon={
            publishing ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : undefined
          }
          sx={{
            textTransform: "none",
            fontFamily: dt.font,
            fontWeight: 600,
            fontSize: "13px",
            borderRadius: "8px",
            background: accentGradientDark,
            boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
            "&:hover": {
              background: accentGradientDarkHover,
              boxShadow: "0 4px 12px rgba(52,152,219,0.3)",
            },
            "&.Mui-disabled": {
              background: "#D1D5DB",
              color: "#9CA3AF",
            },
          }}
        >
          {translate("servicing:card.publishDialog.publish")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishServiceRequestDialog;
